import {
  INVOICE_BASIS_LABELS,
  INVOICE_DIRECTION_LABELS,
  INVOICE_SCENARIO_LABELS,
  INVOICE_SETTLEMENT_LABELS,
  INVOICE_TYPE_LABELS,
  type Invoice,
  PRICING_MODE_LABELS,
} from "@station/contracts";
import { Decimal } from "@station/core";
import {
  Alert,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  MoneyText,
  Select,
  SkeletonRows,
  useToast,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ChevronDown, Download, FileCode2, Pencil, RefreshCw, Send, Undo2 } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../../features/accounting/company.js";
import {
  ReasonDialog,
  SimpleTable,
  tdClass,
  tdRightClass,
  thClass,
  thRightClass,
} from "../../../../features/counterparties/shared.js";
import { useDespatches, useInvoice, useInvoiceSubmissions } from "../../../../features/invoicing/queries.js";
import {
  downloadDocumentFile,
  EdocumentStatusBadge,
  Field,
  InvoiceStatusBadge,
  InvoicingPage,
  LedgerStatusBadge,
  Section,
  useInvoicingCrumb,
  useInvoicingPermissions,
  useRefreshInvoicing,
} from "../../../../features/invoicing/shared.js";
import { api } from "../../../../lib/api.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatDate, formatDateTime, formatUnitPrice } from "../../../../lib/format.js";
import { useStepUp } from "../../../../lib/step-up.js";

export const Route = createFileRoute("/_app/faturalar/$id/")({
  validateSearch: (search) => z.object(companySearchShape).parse(search),
  component: InvoiceDetailPage,
});

function InvoiceDetailPage() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const crumb = useInvoicingCrumb("/faturalar");
  const detail = useInvoice(id);
  const invoice = detail.data;
  const title = invoice?.invoiceNo ?? t("invoicing.detail.draftTitle");

  return (
    <InvoicingPage
      title={title}
      {...(invoice
        ? {
            description: `${INVOICE_DIRECTION_LABELS[invoice.direction]} · ${INVOICE_TYPE_LABELS[invoice.invoiceType]} · ${INVOICE_SCENARIO_LABELS[invoice.scenario]}`,
          }
        : {})}
      breadcrumb={[crumb, { label: title }]}
      actions={invoice ? <InvoiceActions invoice={invoice} /> : null}
    >
      {() =>
        detail.error ? (
          <Alert tone="danger">{errorMessage(detail.error)}</Alert>
        ) : !invoice ? (
          <SkeletonRows rows={8} />
        ) : (
          <InvoiceBody invoice={invoice} />
        )
      }
    </InvoicingPage>
  );
}

function InvoiceActions({ invoice }: { invoice: Invoice }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const refresh = useRefreshInvoicing();
  const permissions = useInvoicingPermissions();
  const withStepUp = useStepUp();
  const [busy, setBusy] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);

  const canWrite = invoice.direction === "SALES" ? permissions.canManageSales : permissions.canManagePurchase;
  const isDraft = invoice.status === "DRAFT";
  const canResend =
    invoice.status === "ISSUED" &&
    (invoice.edocumentStatus === "ERROR" || invoice.edocumentStatus === "QUEUED");
  const canRefresh =
    invoice.status === "ISSUED" && !["NOT_APPLICABLE", "CANCELLED"].includes(invoice.edocumentStatus);

  async function run(kind: string, action: () => Promise<void>): Promise<void> {
    setBusy(kind);
    try {
      await action();
    } catch (error) {
      toast({ tone: "danger", title: errorMessage(error) });
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      {isDraft && canWrite ? (
        <Button asChild variant="outline">
          <Link to="/faturalar/$id/duzenle" params={{ id: invoice.id }}>
            <Pencil aria-hidden="true" />
            {t("common.edit")}
          </Link>
        </Button>
      ) : null}
      {canResend && permissions.canSend ? (
        <Button
          variant="outline"
          loading={busy === "send"}
          onClick={() =>
            void run("send", async () => {
              await api.POST("/invoices/{id}/send", {
                params: { path: { id: invoice.id } },
                body: { version: invoice.version },
              });
              await refresh(invoice.companyId);
              toast({ tone: "success", title: t("invoicing.list.resendQueued") });
            })
          }
        >
          <Send aria-hidden="true" />
          {t("invoicing.actions.resend")}
        </Button>
      ) : null}
      {canRefresh ? (
        <Button
          variant="outline"
          loading={busy === "refresh"}
          onClick={() =>
            void run("refresh", async () => {
              await api.POST("/invoices/{id}/refresh-status", { params: { path: { id: invoice.id } } });
              await refresh(invoice.companyId);
              toast({ tone: "success", title: t("invoicing.detail.statusRefreshed") });
            })
          }
        >
          <RefreshCw aria-hidden="true" />
          {t("invoicing.actions.refreshStatus")}
        </Button>
      ) : null}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" loading={busy === "pdf" || busy === "xml"}>
            {t("common.actions")}
            <ChevronDown aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>{t("invoicing.detail.documentActions")}</DropdownMenuLabel>
          <DropdownMenuItem
            onSelect={() =>
              void run("pdf", async () => {
                const { data } = await api.GET("/invoices/{id}/pdf", {
                  params: { path: { id: invoice.id } },
                });
                downloadDocumentFile(data!);
              })
            }
          >
            <Download aria-hidden="true" />
            {t("invoicing.actions.pdf")}
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() =>
              void run("xml", async () => {
                const { data } = await api.GET("/invoices/{id}/xml", {
                  params: { path: { id: invoice.id } },
                });
                downloadDocumentFile(data!);
              })
            }
          >
            <FileCode2 aria-hidden="true" />
            {t("invoicing.actions.xml")}
          </DropdownMenuItem>
          {invoice.status === "ISSUED" && canWrite ? (
            <DropdownMenuItem
              onSelect={() => void navigate({ to: "/faturalar/yeni", search: { iade: invoice.id } })}
            >
              <Undo2 aria-hidden="true" />
              {t("invoicing.actions.createReturn")}
            </DropdownMenuItem>
          ) : null}
          {invoice.status !== "CANCELLED" && permissions.canCancel ? (
            <DropdownMenuItem onSelect={() => setCancelOpen(true)}>
              {t("invoicing.actions.cancel")}
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      {isDraft && canWrite ? (
        <Button
          loading={busy === "issue"}
          onClick={() =>
            void run("issue", async () => {
              const { data } = await api.POST("/invoices/{id}/issue", {
                params: { path: { id: invoice.id } },
                body: { version: invoice.version },
              });
              await refresh(invoice.companyId);
              toast({ tone: "success", title: t("invoicing.form.issued", { no: data!.invoiceNo ?? "" }) });
            })
          }
        >
          {t("invoicing.actions.issue")}
        </Button>
      ) : null}

      <ReasonDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title={t("invoicing.detail.cancelTitle")}
        description={t("invoicing.detail.cancelDescription")}
        confirmLabel={t("invoicing.actions.cancel")}
        onConfirm={async (reason) => {
          // İptal hassas bir işlemdir: sunucu `REAUTHENTICATION_REQUIRED` derse step-up penceresi açılır.
          await withStepUp(() =>
            api.POST("/invoices/{id}/cancel", {
              params: { path: { id: invoice.id } },
              body: { reason, version: invoice.version },
            }),
          );
          await refresh(invoice.companyId);
          toast({ tone: "success", title: t("invoicing.detail.cancelled") });
        }}
      />
    </>
  );
}

function InvoiceBody({ invoice }: { invoice: Invoice }): ReactElement {
  const { t } = useTranslation();
  const submissions = useInvoiceSubmissions(invoice.id);

  return (
    <div className="grid gap-4">
      {invoice.status === "CANCELLED" ? (
        <Alert tone="warning" title={t("invoicing.detail.cancelledTitle")}>
          {invoice.cancelReason ?? ""}
        </Alert>
      ) : null}
      {invoice.basis === "FISCAL_RECEIPTS" ? (
        <Alert tone="info" title={t("invoicing.receipts.noRevenueTitle")}>
          {t("invoicing.receipts.noRevenueBody")}
        </Alert>
      ) : null}
      {invoice.edocumentError ? (
        <Alert tone="danger" title={t("invoicing.detail.edocumentErrorTitle")}>
          {invoice.edocumentError}
        </Alert>
      ) : null}
      {invoice.ledgerStatus === "QUEUED" ? (
        <Alert tone="warning" title={t("invoicing.list.ledgerQueuedTitle")}>
          {t("invoicing.detail.ledgerQueuedBody")}{" "}
          <Link to="/muhasebe/eslestirmeler" className="underline underline-offset-4">
            {t("menu.accountMappings")}
          </Link>
        </Alert>
      ) : null}

      <Section title={t("invoicing.detail.identity")}>
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label={t("common.status")}>
            <span className="flex flex-wrap gap-1.5">
              <InvoiceStatusBadge status={invoice.status} />
              <EdocumentStatusBadge status={invoice.edocumentStatus} error={invoice.edocumentError} />
              <LedgerStatusBadge status={invoice.ledgerStatus} />
            </span>
          </Field>
          <Field label={t("invoicing.fields.counterparty")}>
            <Link
              to="/cari/$id"
              params={{ id: invoice.counterpartyId }}
              className="text-primary underline-offset-4 hover:underline"
            >
              {invoice.counterpartyName}
            </Link>
          </Field>
          <Field label={t("invoicing.fields.issueDate")}>
            <span className="tabular">
              {formatDate(invoice.issueDate)} {invoice.issueTime.slice(0, 5)}
            </span>
          </Field>
          <Field label={t("invoicing.fields.dueDate")}>
            <span className="tabular">{formatDate(invoice.dueDate)}</span>
          </Field>
          <Field label={t("invoicing.fields.ettn")}>
            <span className="font-mono text-xs">{invoice.ettn}</span>
          </Field>
          <Field label={t("invoicing.fields.scenario")}>{INVOICE_SCENARIO_LABELS[invoice.scenario]}</Field>
          <Field label={t("invoicing.fields.basis")}>{INVOICE_BASIS_LABELS[invoice.basis]}</Field>
          <Field label={t("invoicing.fields.pricingMode")}>{PRICING_MODE_LABELS[invoice.pricingMode]}</Field>
          <Field label={t("invoicing.fields.settlement")}>
            {INVOICE_SETTLEMENT_LABELS[invoice.settlement]}
          </Field>
          {invoice.stationName ? (
            <Field label={t("invoicing.fields.station")}>{invoice.stationName}</Field>
          ) : null}
          {invoice.originalInvoiceId ? (
            <Field label={t("invoicing.detail.originalInvoice")}>
              <Link
                to="/faturalar/$id"
                params={{ id: invoice.originalInvoiceId }}
                className="font-mono text-primary underline-offset-4 hover:underline"
              >
                {invoice.originalInvoiceNo ?? invoice.originalInvoiceId.slice(0, 8)}
              </Link>
            </Field>
          ) : null}
          {invoice.notes ? <Field label={t("invoicing.fields.notes")}>{invoice.notes}</Field> : null}
        </dl>
        <div className="flex flex-wrap gap-2 border-t pt-3">
          <Button asChild variant="outline" size="sm">
            <Link to="/cari/$id" params={{ id: invoice.counterpartyId }} search={{ tab: "open-items" }}>
              {t("invoicing.detail.openItems")}
            </Link>
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to="/cari/raporlar" search={{ tab: "statement", counterparty: invoice.counterpartyId }}>
              {t("invoicing.detail.statement")}
            </Link>
          </Button>
          {invoice.journalEntries.map((entry) => (
            <Button asChild variant="outline" size="sm" key={entry.id}>
              <Link to="/muhasebe/fisler/$id" params={{ id: entry.id }}>
                {t("invoicing.detail.journalEntry", { no: entry.entryNo ?? entry.purpose })}
              </Link>
            </Button>
          ))}
        </div>
      </Section>

      <Section title={t("invoicing.detail.linesTitle")}>
        <SimpleTable
          caption={t("invoicing.detail.linesTitle")}
          head={
            <tr>
              <th scope="col" className={thClass}>
                #
              </th>
              <th scope="col" className={thClass}>
                {t("invoicing.fields.description")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("invoicing.fields.quantity")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("invoicing.fields.unitPrice")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("invoicing.fields.discount")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("invoicing.fields.netAmount")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("invoicing.fields.vatAmount")}
              </th>
              <th scope="col" className={thRightClass}>
                {t("invoicing.totals.withheld")}
              </th>
            </tr>
          }
        >
          {invoice.lines.map((line) => (
            <tr key={line.id} className="border-t">
              <td className={tdClass}>{line.lineNo}</td>
              <td className={tdClass}>
                {line.productCode ? (
                  <span className="font-mono text-xs text-muted-foreground">{line.productCode} </span>
                ) : null}
                {line.description}
                {line.exemptionCode ? (
                  <span className="block text-xs text-muted-foreground">
                    {t("invoicing.fields.exemptionCode")}: {line.exemptionCode}
                  </span>
                ) : null}
                {line.withholdingCode ? (
                  <span className="block text-xs text-muted-foreground">
                    {t("invoicing.fields.withholdingCode")}: {line.withholdingCode} ·{" "}
                    {line.withholdingNumerator}/{line.withholdingDenominator}
                  </span>
                ) : null}
              </td>
              <td className={tdRightClass}>{Decimal.from(line.quantity).formatTr(3)}</td>
              <td className={tdRightClass}>{formatUnitPrice(line.unitPrice)}</td>
              <td className={tdRightClass}>
                <MoneyText value={line.lineDiscountAmount} />
              </td>
              <td className={tdRightClass}>
                <MoneyText value={line.netAmount} />
              </td>
              <td className={tdRightClass}>
                <MoneyText value={line.vatAmount} />
              </td>
              <td className={tdRightClass}>
                <MoneyText value={line.withheldAmount} />
              </td>
            </tr>
          ))}
        </SimpleTable>

        <dl className="ml-auto grid w-full max-w-sm gap-1.5 text-sm">
          <TotalRow label={t("invoicing.totals.grossNet")} value={invoice.totals.grossNetAmount} />
          <TotalRow label={t("invoicing.totals.discount")} value={invoice.totals.discountNetAmount} />
          <TotalRow label={t("invoicing.totals.net")} value={invoice.totals.netAmount} />
          {invoice.taxTotals.map((tax) => (
            <TotalRow
              key={tax.vatRate}
              label={t("invoicing.totals.vatAt", { rate: Decimal.from(tax.vatRate).formatTr(0) })}
              value={tax.vatAmount}
            />
          ))}
          {invoice.withholdingTotals.map((item) => (
            <TotalRow
              key={item.code}
              label={t("invoicing.totals.withholdingAt", {
                code: item.code,
                ratio: `${item.numerator}/${item.denominator}`,
              })}
              value={item.withheldAmount}
            />
          ))}
          <TotalRow label={t("invoicing.totals.payable")} value={invoice.totals.payableAmount} strong />
        </dl>
      </Section>

      {invoice.receipts.length > 0 ? (
        <Section title={t("invoicing.receipts.title")}>
          <SimpleTable
            caption={t("invoicing.receipts.caption")}
            head={
              <tr>
                <th scope="col" className={thClass}>
                  {t("invoicing.receipts.deviceSerial")}
                </th>
                <th scope="col" className={thClass}>
                  {t("invoicing.receipts.zNo")}
                </th>
                <th scope="col" className={thClass}>
                  {t("invoicing.receipts.receiptNo")}
                </th>
                <th scope="col" className={thClass}>
                  {t("invoicing.receipts.receiptDate")}
                </th>
                <th scope="col" className={thRightClass}>
                  {t("invoicing.receipts.amount")}
                </th>
              </tr>
            }
          >
            {invoice.receipts.map((receipt) => (
              <tr key={receipt.id} className="border-t">
                <td className={tdClass}>{receipt.fiscalDeviceSerial}</td>
                <td className={tdClass}>{receipt.zNo}</td>
                <td className={tdClass}>{receipt.receiptNo}</td>
                <td className={tdClass}>{formatDate(receipt.receiptDate)}</td>
                <td className={tdRightClass}>
                  <MoneyText value={receipt.amount} />
                </td>
              </tr>
            ))}
          </SimpleTable>
        </Section>
      ) : null}

      <DespatchLinks invoice={invoice} />

      {invoice.returnInvoices.length > 0 ? (
        <Section title={t("invoicing.detail.returnsTitle")}>
          <ul className="grid gap-1 text-sm">
            {invoice.returnInvoices.map((item) => (
              <li key={item.id}>
                <Link
                  to="/faturalar/$id"
                  params={{ id: item.id }}
                  className="font-mono text-primary underline-offset-4 hover:underline"
                >
                  {item.invoiceNo ?? t("invoicing.list.draftNo")}
                </Link>{" "}
                <span className="text-muted-foreground">{item.status}</span>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      <Section title={t("invoicing.detail.submissionsTitle")}>
        {submissions.isPending ? (
          <SkeletonRows rows={3} />
        ) : submissions.error ? (
          <Alert tone="danger">{errorMessage(submissions.error)}</Alert>
        ) : (submissions.data ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("invoicing.detail.noSubmissions")}</p>
        ) : (
          <SimpleTable
            caption={t("invoicing.detail.submissionsTitle")}
            head={
              <tr>
                <th scope="col" className={thClass}>
                  {t("invoicing.submissions.createdAt")}
                </th>
                <th scope="col" className={thClass}>
                  {t("invoicing.submissions.operation")}
                </th>
                <th scope="col" className={thRightClass}>
                  {t("invoicing.submissions.attempt")}
                </th>
                <th scope="col" className={thClass}>
                  {t("invoicing.submissions.outcome")}
                </th>
                <th scope="col" className={thClass}>
                  {t("invoicing.submissions.error")}
                </th>
                <th scope="col" className={thRightClass}>
                  {t("invoicing.submissions.duration")}
                </th>
              </tr>
            }
          >
            {(submissions.data ?? []).map((submission) => (
              <tr key={submission.id} className="border-t">
                <td className={tdClass}>{formatDateTime(submission.createdAt)}</td>
                <td className={tdClass}>{submission.operation}</td>
                <td className={tdRightClass}>{submission.attempt}</td>
                <td className={tdClass}>{t(`invoicing.submissionOutcome.${submission.outcome}`)}</td>
                <td className={tdClass}>{submission.errorMessage ?? submission.resultingStatus ?? "—"}</td>
                <td className={tdRightClass}>{submission.durationMs} ms</td>
              </tr>
            ))}
          </SimpleTable>
        )}
      </Section>
    </div>
  );
}

function TotalRow({
  label,
  value,
  strong,
}: {
  label: string;
  value: string;
  strong?: boolean;
}): ReactElement {
  return (
    <div className={`flex items-baseline justify-between gap-4 ${strong ? "border-t pt-2" : ""}`}>
      <dt className={strong ? "font-semibold" : "text-muted-foreground"}>{label}</dt>
      <dd>
        <MoneyText value={value} className={strong ? "font-semibold" : undefined} />
      </dd>
    </div>
  );
}

/** Bağlı irsaliyeler: aynı carinin faturaya bağlanmamış irsaliyeleri seçilerek eklenir / kaldırılır. */
function DespatchLinks({ invoice }: { invoice: Invoice }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshInvoicing();
  const permissions = useInvoicingPermissions();
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);
  const editable = invoice.status !== "CANCELLED" && permissions.canManageDespatch;
  const candidates = useDespatches(
    invoice.companyId,
    { counterpartyId: invoice.counterpartyId, status: "ISSUED", unlinkedOnly: true, limit: 50 },
    undefined,
    editable,
  );

  async function setLinks(ids: string[]): Promise<void> {
    setBusy(true);
    try {
      await api.PUT("/invoices/{id}/despatches", {
        params: { path: { id: invoice.id } },
        body: { despatchIds: ids },
      });
      await refresh(invoice.companyId);
      setSelected("");
      toast({ tone: "success", title: t("common.saved") });
    } catch (error) {
      toast({ tone: "danger", title: errorMessage(error) });
    } finally {
      setBusy(false);
    }
  }

  const linkedIds = invoice.despatches.map((despatch) => despatch.id);

  return (
    <Section title={t("invoicing.detail.despatchesTitle")}>
      {invoice.despatches.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("invoicing.detail.noDespatches")}</p>
      ) : (
        <ul className="grid gap-1 text-sm">
          {invoice.despatches.map((despatch) => (
            <li key={despatch.id} className="flex items-center gap-3">
              <Link
                to="/faturalar/irsaliyeler/$id"
                params={{ id: despatch.id }}
                className="font-mono text-primary underline-offset-4 hover:underline"
              >
                {despatch.despatchNo ?? despatch.id.slice(0, 8)}
              </Link>
              <span className="tabular text-muted-foreground">{formatDate(despatch.issueDate)}</span>
              {editable ? (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() => void setLinks(linkedIds.filter((id) => id !== despatch.id))}
                >
                  {t("invoicing.detail.unlinkDespatch")}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {editable ? (
        <div className="flex flex-wrap items-end gap-2">
          <label className="grid gap-1.5 text-sm font-medium">
            {t("invoicing.detail.linkDespatch")}
            <Select
              className="w-72"
              value={selected}
              onChange={(event) => setSelected(event.target.value)}
              disabled={busy}
            >
              <option value="">{t("common.select")}</option>
              {(candidates.data?.items ?? []).map((despatch) => (
                <option key={despatch.id} value={despatch.id}>
                  {despatch.despatchNo ?? despatch.id.slice(0, 8)} · {formatDate(despatch.issueDate)}
                </option>
              ))}
            </Select>
          </label>
          <Button
            variant="outline"
            disabled={!selected || busy}
            onClick={() => void setLinks([...linkedIds, selected])}
          >
            {t("invoicing.detail.addLink")}
          </Button>
        </div>
      ) : null}
    </Section>
  );
}
